import type {Group, Material, Mesh} from 'three'
import {lerp} from './utils/math'
import type Common from './common'
import Timeline from './utils/timeline'
import {Vector3} from 'three'

export default class Animations {
  animationGroup: Group
  goggleGroup: Group
  eyesMeshes: {left: Mesh | null; right: Mesh | null}
  isPlayingClickAnim: boolean = false
  isBlinking: boolean = false
  declare blinkTimer: NodeJS.Timeout
  common?: Common

  constructor({
    animationGroup,
    goggleGroup,
    eyesMeshes,
    common,
  }: {
    animationGroup: Group
    goggleGroup: Group
    eyesMeshes: {left: Mesh | null; right: Mesh | null}
    common: Common
  }) {
    this.common = common
    this.goggleGroup = goggleGroup
    this.eyesMeshes = eyesMeshes
    this.animationGroup = animationGroup

    this.init()
  }

  init() {
    this.createIntroAnimation()
  }

  createIntroAnimation() {
    this.createBlinkEyes()
  }

  createBlinkEyes() {
    this.isBlinking = true
    const tl = new Timeline({
      onComplete: () => {
        this.isBlinking = false
        const delay = lerp(2, 6, Math.random()) * 1000
        this.blinkTimer = setTimeout(() => {
          this.createBlinkEyes()
        }, delay)
      },
      easing: 'easeOutCubic',
    })
    const type = Math.random() < 0.8 ? 'blinkType1' : 'blinkType2'

    const bs = 2 / 1000
    const leftEye = this.eyesMeshes.left
    const rightEye = this.eyesMeshes.right
    const eyesScale = [leftEye ? leftEye.scale : 1, rightEye ? rightEye.scale : 1]

    if (type === 'blinkType1') {
      tl.to(eyesScale, 0.1 / bs, {y: 0.1}, 0)
        .to(eyesScale, 0.3 / bs, {y: 1}, 0.2 / bs)
        .start()
    } else {
      tl.to(eyesScale, 0.1 / bs, {y: 0.1}, 0)
        .to(eyesScale, 0.2 / bs, {y: 1}, 0.2 / bs)
        .to(eyesScale, 0.3 / bs, {y: 0.1})
        .to(eyesScale, 0.3 / bs, {y: 1})
        .start()
    }
  }

  createClickAnimation() {
    if (this.isPlayingClickAnim) return
    this.createJump()
  }

  createJump() {
    if (this.isPlayingClickAnim) return
    this.isPlayingClickAnim = true

    if (!this.isBlinking) {
      if (this.blinkTimer) clearTimeout(this.blinkTimer)
      this.createSmile()
    }

    const data = {
      anim_rotating: this.goggleGroup.userData.rotateX.anim_rotating,
    }

    const tl = new Timeline({
      onComplete: () => {
        this.isPlayingClickAnim = false
      },
      onUpdate: () => {
        this.goggleGroup.userData.rotateX.anim_rotating = data.anim_rotating
      },
      easing: 'easeOutCubic',
    })

    const s = 1000
    const gs = this.animationGroup.scale
    const ag = this.animationGroup.position
    const aGR = this.animationGroup.rotation

    tl.to(gs, 0.3 * s, {y: 0.95, x: 1.05}, 0)
      .to(gs, 0.3 * s, {y: 1.03, x: 0.97})
      .to(gs, 0.3 * s, {y: 0.98, x: 1.02})
      .to(gs, 0.3 * s, {y: 1.015, x: 0.985})
      .to(gs, 0.3 * s, {y: 0.97, x: 1.03})
      .to(gs, 0.3 * s, {y: 1, x: 1})

      .to(aGR, 200, {x: 0.2}, 0)
      .to(aGR, 500, {x: -0.1}, 200)
      .to(aGR, 300, {x: 0.0}, 700)
      .to(ag, 0.1 * s, {y: -0.2}, 0)
      .to(ag, 0.3 * s, {y: 1.8}, 0.1 * s)
      .to(ag, 0.3 * s, {y: 0, easing: 'easeInQuad'}, 0.4 * s)
      .to(ag, 0.3 * s, {y: 0.8}, 0.7 * s)

      .to(ag, 0.2 * s, {y: -0.4, easing: 'easeInQuad'}, 1.0 * s)
      .to(ag, 0.2 * s, {y: 0}, 1.2 * s)

    const gGP = [this.goggleGroup.position]

    // goggle animation
    tl.to(gGP, 0.3 * s, {y: 0.5}, 0.2 * s)
      .to(gGP, 0.2 * s, {y: 0.0}, 0.6 * s)
      .to(gGP, 0.3 * s, {y: 0.3}, 0.8 * s)
      .to(gGP, 0.4 * s, {y: 0.0}, 1.1 * s)

      .to([data], 0.2 * s, {anim_rotating: 0.1}, 1.1 * s)
      .to([data], 0.2 * s, {anim_rotating: 0}, 1.3 * s)
      .start()
  }

  createSmile() {
    const tl = new Timeline({
      onComplete: () => {
        const delay = lerp(1, 4, Math.random()) * 1000
        this.blinkTimer = setTimeout(() => {
          this.createBlinkEyes()
        }, delay)
      },
      easing: 'easeOutQuad',
    })

    const leftEye = this.eyesMeshes.left
    const rightEye = this.eyesMeshes.right
    const eyeScales = [leftEye ? leftEye.scale : new Vector3(1, 1, 1), rightEye ? rightEye.scale : new Vector3(1, 1, 1)]
    const uWink = [
      (leftEye!.material as Material).userData.uniforms.uWink,
      (rightEye!.material as Material).userData.uniforms.uWink,
    ]

    const defY = leftEye ? leftEye.position.y : 0

    const position = [
      leftEye ? leftEye.position : new Vector3(0, 0, 0),
      rightEye ? rightEye.position : new Vector3(0, 0, 0),
    ]

    const delay = 1500

    tl.to(eyeScales, 100, {y: 0.6}, 0)
      .to(eyeScales, 250, {y: 1}, delay)
      .to(uWink, 100, {value: 0.3}, 0)
      .to(uWink, 100, {value: 0}, delay)
      .to(position, 200, {y: defY + 0.1}, 0)
      .to(position, 200, {y: defY}, delay)
      .start()
  }
  resize() {}
  update() {}
}
